/**
 * Card templates — DESIGN.md §6.15.
 *
 * "New card from template" pre-fills description, subtasks, tags, custom
 * fields and estimate; the template is applied server-side so a template and a
 * hand-made card go through exactly the same creation path.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Template, TemplateFromTaskRequest } from '@slate/shared';
import { http } from '@/lib/api';

export const templatesKey = ['templates'] as const;

export function useTemplates(projectId?: string | null): UseQueryResult<Template[], Error> {
  return useQuery({
    queryKey: [...templatesKey, projectId ?? 'global'] as const,
    queryFn: () => http.get<Template[]>('/templates', { query: projectId ? { projectId } : {} }),
    staleTime: 5 * 60_000,
  });
}

/** §6.15 — "save this card as a template", from the card's actions menu. */
export function useTemplateFromTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TemplateFromTaskRequest) =>
      http.post<Template>('/templates/from-task', input),
    onSuccess: async (template) => {
      await queryClient.invalidateQueries({ queryKey: templatesKey });
      toast.success(`Saved “${template.name}” as a template`);
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * What a template will bring with it, in one line — shown under the picker so
 * choosing one is not a leap of faith.
 */
export function describeTemplate(template: Template): string {
  const parts: string[] = [];
  if (template.content.description) parts.push('a description');
  if (template.content.subtasks.length > 0) {
    parts.push(
      `${template.content.subtasks.length} subtask${template.content.subtasks.length === 1 ? '' : 's'}`,
    );
  }
  if (template.content.tagIds.length > 0) {
    parts.push(
      `${template.content.tagIds.length} tag${template.content.tagIds.length === 1 ? '' : 's'}`,
    );
  }
  const fieldCount = Object.keys(template.content.fieldValues).length;
  if (fieldCount > 0) parts.push(`${fieldCount} custom field${fieldCount === 1 ? '' : 's'}`);
  if (template.content.priority) parts.push(`priority ${template.content.priority}`);
  if (template.content.estimateMinutes) {
    parts.push(`a ${Math.round(template.content.estimateMinutes / 60)}h estimate`);
  }

  if (parts.length === 0) return 'Fills in the title only.';
  const last = parts.pop()!;
  return `Adds ${parts.length > 0 ? `${parts.join(', ')} and ${last}` : last}. Anything you set here wins.`;
}
